<template>
  <div class="compound-view-container">
    <MainHeader title="Compound Interest">
      <template #left>
        <font-awesome-icon
          @click="router.back()"
          :icon="['fas', 'arrow-left-long']"
          class="back-arrow pointer"
          role="button"
          aria-label="Go back"
        />
      </template>
    </MainHeader>

    <div class="view content content-padding">
      <div class="ud-section">
        <div class="hero-strip">
          <div class="hero-icon-wrap">
            <font-awesome-icon :icon="['fas', 'chart-line']" />
          </div>
          <div class="hero-text">
            <h2>Compound Interest</h2>
            <p>
              Project growth over time with contributions and a return rate.
            </p>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Starting balance</h3>
          <div class="ud-field">
            <label class="ud-label">Initial investment</label>
            <div class="input-with-prefix">
              <span class="prefix">$</span>
              <input
                v-model.number="inputs.principal"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Contributions</h3>
          <div class="ud-field">
            <label class="ud-label">Contribution amount</label>
            <div class="input-with-prefix">
              <span class="prefix">$</span>
              <input
                v-model.number="inputs.contribution"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="0"
              />
            </div>
          </div>

          <div class="ud-field">
            <label class="ud-label">Frequency</label>
            <div class="mode-pills">
              <button
                v-for="f in frequencies"
                :key="f.value"
                type="button"
                :class="['mode-pill', { active: inputs.frequency === f.value }]"
                @click="inputs.frequency = f.value"
              >
                {{ f.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Growth assumptions</h3>
          <div class="dual-input">
            <div class="ud-field flex-1">
              <label class="ud-label">Annual return rate</label>
              <div class="input-with-suffix">
                <input
                  v-model.number="inputs.rate"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                  step="0.1"
                />
                <span class="suffix">%</span>
              </div>
            </div>
            <div class="ud-field flex-1">
              <label class="ud-label">Years to grow</label>
              <input
                v-model.number="inputs.years"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="0"
              />
            </div>
          </div>
          <p class="field-hint">
            Historical average stock market return is about 10% before
            inflation, 7% after.
          </p>
        </div>

        <div v-if="hasResults" class="wrapper-card results-card">
          <h3 class="section-label">Projected value</h3>
          <div class="big-number">
            {{ formatCurrency(result.futureValue) }}
            <span class="big-number-sub">in {{ inputs.years }} years</span>
          </div>

          <div class="piti-breakdown">
            <div class="piti-row">
              <span class="piti-label">Total contributions</span>
              <span class="piti-value">{{
                formatCurrency(result.totalContributions)
              }}</span>
            </div>
            <div class="piti-row">
              <span class="piti-label">Interest earned</span>
              <span class="piti-value" :class="'hint-good'">{{
                formatCurrency(result.totalInterest)
              }}</span>
            </div>
            <div class="piti-row">
              <span class="piti-label">Starting amount</span>
              <span class="piti-value">{{
                formatCurrency(inputs.principal || 0)
              }}</span>
            </div>
          </div>

          <div class="result-stats">
            <div class="stat">
              <span class="stat-label">Money multiplied</span>
              <span class="stat-value">{{ result.multiplier }}x</span>
            </div>
            <div class="stat">
              <span class="stat-label">% from growth</span>
              <span class="stat-value">{{ result.percentFromGrowth }}%</span>
            </div>
          </div>
        </div>

        <div v-if="hasResults" class="wrapper-card">
          <h3 class="section-label">Year by year</h3>
          <div class="schedule-table">
            <div class="schedule-header">
              <span>Year</span>
              <span>Balance</span>
              <span>Contributions</span>
              <span>Interest</span>
            </div>
            <div
              v-for="row in result.schedule"
              :key="row.year"
              class="schedule-row"
              :class="{ 'milestone-row': row.isMilestone }"
            >
              <span>{{ row.year }}</span>
              <span>{{ formatCurrency(row.balance) }}</span>
              <span>{{ formatCurrency(row.yearlyContribution) }}</span>
              <span :class="'hint-good'">{{
                formatCurrency(row.yearlyInterest)
              }}</span>
            </div>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Reverse calculator</h3>
          <p class="section-blurb">How long to reach a target amount?</p>
          <div class="ud-field">
            <label class="ud-label">Target amount</label>
            <div class="input-with-prefix">
              <span class="prefix">$</span>
              <input
                v-model.number="targetGoal"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="0"
              />
            </div>
          </div>
          <div
            v-if="targetResult"
            class="afford-verdict"
            :class="targetResult.class"
          >
            <font-awesome-icon :icon="targetResult.icon" />
            <span>{{ targetResult.message }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import MainHeader from '../../components/View-Header.vue';

export default {
  name: 'CalculatorCompoundInterest',
  components: { MainHeader },
  setup() {
    const router = useRouter();

    const inputs = ref({
      principal: null,
      contribution: null,
      frequency: 'monthly',
      rate: null,
      years: null,
    });

    const frequencies = [
      { value: 'monthly', label: 'Monthly' },
      { value: 'annual', label: 'Annual' },
    ];

    const targetGoal = ref(null);

    const hasResults = computed(() => {
      return (
        (inputs.value.principal || 0) >= 0 &&
        (inputs.value.rate || 0) > 0 &&
        (inputs.value.years || 0) > 0
      );
    });

    const periodsPerYear = computed(() => {
      return inputs.value.frequency === 'monthly' ? 12 : 1;
    });

    const contributionPerPeriod = computed(() => {
      const c = inputs.value.contribution || 0;
      return inputs.value.frequency === 'monthly' ? c : c;
    });

    const result = computed(() => {
      const P = inputs.value.principal || 0;
      const r = (inputs.value.rate || 0) / 100;
      const t = inputs.value.years || 0;
      const n = periodsPerYear.value;
      const PMT = contributionPerPeriod.value;

      if (r <= 0 || t <= 0) {
        return {
          futureValue: 0,
          totalContributions: 0,
          totalInterest: 0,
          multiplier: 0,
          percentFromGrowth: 0,
          schedule: [],
        };
      }

      const schedule = [];
      let balance = P;
      let totalContributions = P;
      let totalInterest = 0;

      for (let year = 1; year <= t; year++) {
        const startBalance = balance;
        for (let period = 0; period < n; period++) {
          balance = balance * (1 + r / n);
          if (PMT > 0) {
            balance += PMT;
            totalContributions += PMT;
          }
        }
        const yearlyInterest = balance - startBalance - PMT * n;
        totalInterest += yearlyInterest;

        const isMilestone = [5, 10, 20, 30].includes(year) || year === t;

        schedule.push({
          year,
          balance,
          yearlyContribution: PMT * n,
          yearlyInterest,
          isMilestone,
        });
      }

      const futureValue = balance;
      const multiplier = P > 0 ? (futureValue / P).toFixed(1) : '0';
      const percentFromGrowth =
        futureValue > 0 ? Math.round((totalInterest / futureValue) * 100) : 0;

      return {
        futureValue,
        totalContributions,
        totalInterest,
        multiplier,
        percentFromGrowth,
        schedule,
      };
    });

    const targetResult = computed(() => {
      const goal = targetGoal.value;
      const P = inputs.value.principal || 0;
      const r = (inputs.value.rate || 0) / 100;
      const n = periodsPerYear.value;
      const PMT = contributionPerPeriod.value;

      if (!goal || goal <= 0 || r <= 0) return null;

      let balance = P;
      let years = 0;
      const maxYears = 100;

      while (balance < goal && years < maxYears) {
        years++;
        for (let period = 0; period < n; period++) {
          balance = balance * (1 + r / n);
          if (PMT > 0) balance += PMT;
        }
      }

      if (years >= maxYears && balance < goal) {
        return {
          class: 'bad',
          icon: ['fas', 'circle-exclamation'],
          message: `Even after ${maxYears} years, you'd only have ${formatCurrency(balance)}. Increase contributions or rate.`,
        };
      }

      return {
        class: 'good',
        icon: ['fas', 'circle-check'],
        message: `You'll reach ${formatCurrency(goal)} in ${years} year${years === 1 ? '' : 's'} at this pace.`,
      };
    });

    function formatCurrency(n) {
      if (!isFinite(n) || n === null || n === undefined) return '$0';
      return '$' + Math.round(n).toLocaleString('en-US');
    }

    return {
      router,
      inputs,
      frequencies,
      targetGoal,
      hasResults,
      result,
      targetResult,
      formatCurrency,
    };
  },
};
</script>

<style scoped>
.compound-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.back-arrow {
  font-size: 18px;
  color: var(--color-primary);
  cursor: pointer;
  transition: transform var(--transition-fast);
}
.back-arrow:hover {
  transform: translateX(-2px);
}
.dark-theme .back-arrow {
  color: var(--color-accent-teal);
}

.pointer {
  cursor: pointer;
}

.hero-strip {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(
    135deg,
    var(--purple-50) 0%,
    var(--teal-50, #e5f7f1) 100%
  );
  border-radius: var(--radius-lg);
  margin-bottom: 16px;
}
.hero-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  background: var(--color-accent-purple);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.hero-icon-wrap font-awesome-icon {
  color: white;
  font-size: 24px;
}
.hero-text h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  margin: 0 0 4px 0;
}
.hero-text p {
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

.ud-section {
  padding: 16px;
  padding-bottom: 80px;
}

.wrapper-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #f0f0f0;
}
.dark-theme .wrapper-card {
  background: #212e3e;
  border-color: #2a3a4e;
}

.section-label {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
}
.section-blurb {
  font-size: 13px;
  margin: 0 0 16px 0;
  line-height: 1.4;
}

.light-theme .section-blurb {
  color: #666;
}

.ud-field {
  margin-bottom: 14px;
}
.ud-field:last-child {
  margin-bottom: 0;
}
.ud-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 6px;
}

.input-with-prefix,
.input-with-suffix {
  position: relative;
  display: flex;
  align-items: center;
}
.input-with-prefix .input {
  padding-left: 28px;
}
.input-with-suffix .input {
  padding-right: 32px;
}
.prefix,
.suffix {
  position: absolute;
  color: #999;
  font-size: 14px;
  pointer-events: none;
}
.prefix {
  left: 12px;
}
.suffix {
  right: 12px;
}

.input {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e5e5e5;
  border-radius: var(--radius-md);
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  color: var(--color-primary, #213547);
  background: white;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
.input:focus {
  border-color: var(--color-accent-purple);
  box-shadow: 0 0 0 3px rgba(127, 119, 221, 0.1);
}
.dark-theme .input {
  background: #1a2535;
  border-color: #2a3a4e;
  color: white;
}

.dual-input {
  display: flex;
  gap: 10px;
}
.flex-1 {
  flex: 1;
}

.field-hint {
  font-size: 12px;
  margin: 6px 0 0 0;
  line-height: 1.4;
}
.light-theme .field-hint {
  color: #888;
}
.hint-good {
  color: var(--color-accent-green, #5dcaa5);
}

.mode-pills {
  display: flex;
  gap: 8px;
}
.mode-pill {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 8px;
  border: 1.5px solid #e5e5e5;
  background: white;
  border-radius: var(--radius-md);
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}
.mode-pill.active {
  background: var(--color-deep-teal, #004751);
  color: white;
  border-color: var(--color-deep-teal, #004751);
  box-shadow: var(--shadow-sm);
}

.results-card {
  background: linear-gradient(135deg, var(--purple-50) 0%, var(--teal-50) 100%);
  border: 1px solid #ece9fb;
}
.dark-theme .results-card {
  background: linear-gradient(135deg, #1e1a40 0%, #0d2420 100%);
  border-color: #2a3a4e;
}

.big-number {
  font-family: 'Poppins', sans-serif;
  font-size: 32px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
}
.big-number-sub {
  font-size: 13px;
  font-weight: 400;
  color: #888;
}

.piti-breakdown {
  background: white;
  border-radius: var(--radius-md);
  padding: 12px 14px;
  margin-bottom: 16px;
}
.dark-theme .piti-breakdown {
  background: #212e3e;
}
.piti-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}
.piti-label {
  color: #555;
}
.piti-value {
  font-weight: 500;
  color: var(--color-deep-teal, #004751);
}

.result-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  background: var(--teal-50, #e5f7f1);
  border-radius: var(--radius-sm);
}
.stat-label {
  font-size: 11px;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.stat-value {
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
}

.schedule-table {
  margin-top: 12px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: var(--radius-sm);
}
.schedule-header,
.schedule-row {
  display: grid;
  grid-template-columns: 50px 1fr 1fr 1fr;
  font-size: 12px;
  padding: 8px 10px;
  border-bottom: 1px solid #f5f5f5;
}
.schedule-header {
  background: var(--purple-50);
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  position: sticky;
  top: 0;
}
.schedule-row.milestone-row {
  background: var(--teal-50, #e5f7f1);
  font-weight: 500;
}

.afford-verdict {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: var(--radius-md);
  font-size: 14px;
  margin-top: 12px;
  background: white;
  line-height: 1.4;
}
.afford-verdict font-awesome-icon {
  font-size: 20px;
  flex-shrink: 0;
}
.afford-verdict.good {
  color: var(--color-accent-green, #5dcaa5);
  border-left: 3px solid var(--color-accent-green);
}
.afford-verdict.bad {
  color: #d45a4a;
  border-left: 3px solid #d45a4a;
}
</style>
